import React from 'react';
import { Box, ButtonBase, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

type SearchTriggerProps = {
  placeholder: string;
  placeholderMobile?: string;
  shortcut: string;
  ariaLabel: string;
  onClick: () => void;
};

const TriggerButton = styled(ButtonBase)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: 'transparent',
  padding: theme.spacing(0.5, 1.5),
  height: 36,
  minWidth: 220,
  flex: '0 1 320px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  justifyContent: 'space-between',
  textAlign: 'left',
  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest
  }),
  '&:hover': {
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[1]
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    flex: '0 0 auto',
    padding: theme.spacing(0.5, 1),
    height: 32
  }
}));

const ShortcutPill = styled('span')(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[400]}`,
  backgroundColor: 'transparent',
  padding: theme.spacing(0.25, 0.75),
  fontSize: theme.typography.pxToRem(12),
  lineHeight: 1.2,
  fontWeight: 500,
  color: theme.palette.text.secondary
}));

const SearchTrigger: React.FC<SearchTriggerProps> = ({
  placeholder,
  placeholderMobile,
  shortcut,
  ariaLabel,
  onClick
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const displayText = isMobile && placeholderMobile ? placeholderMobile : placeholder;

  return (
    <TriggerButton onClick={onClick} aria-label={ariaLabel}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary" noWrap>
          {displayText}
        </Typography>
      </Box>
      {!isMobile && <ShortcutPill aria-hidden="true">{shortcut}</ShortcutPill>}
    </TriggerButton>
  );
};

export default SearchTrigger;
